// Which APNs host this build's device tokens belong to (ART-259). The answer comes from the wrapper's in-app
// `PushEnvironment` plugin (PushEnvironmentPlugin.swift), which reads the SIGNED `aps-environment`. It used to be
// derived from the API lane (PROD → production, else sandbox), which is the wrong axis: a TestFlight build pointed
// at qa is signed for PRODUCTION APNs, so it claimed sandbox and every push to it failed with BadDeviceToken.
import { Capacitor, registerPlugin } from "@capacitor/core";

export type ApnsEnvironment = "production" | "sandbox";

interface PushEnvironmentPlugin {
  get(): Promise<{ environment?: string }>;
}

const PushEnvironment = registerPlugin<PushEnvironmentPlugin>("PushEnvironment");

/**
 * The APNs environment the native build was signed for, or <paramref name="fallback"/> when it cannot be read:
 * on web, on Android, or in a wrapper too old to carry the plugin. The fallback is the lane-derived value the app
 * used before. The server retries the other host once on BadDeviceToken, so a wrong fallback costs a request, not
 * a push.
 */
export async function resolveApnsEnvironment(fallback: ApnsEnvironment): Promise<ApnsEnvironment> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return fallback;
  try {
    const { environment } = await PushEnvironment.get();
    return environment === "production" || environment === "sandbox" ? environment : fallback;
  } catch {
    return fallback; // an older wrapper without the plugin rejects "not implemented"
  }
}
